<script setup lang="ts">
import BaseMultiSelect from '@/components/base/BaseMultiSelect.vue'
import axios from 'axios'

import { filterStoresByScope, resolveAudienceScopedStoreIds, resolveIncludedAudiences } from '@/utils/audienceStoreIds'
import useAudienceStoreScope from '@/composables/useAudienceStoreScope'

import type { Audience } from '@/types/types'

export interface SelectedAudience {
  availableLists: Audience[]
  excludedLists: number[]
  lists: number[]
  stores: {
    value: number
    label: string
    smsName: string
  }[]
}

const count = defineModel<number>('count')
const selectedAudience = defineModel<SelectedAudience>({
  type: Object,
  required: true
})

const props = defineProps<{
  type: 'sms' | 'email' | 'push'
}>()

const { user } = useLocalStorage()
const { getStoresByPermission } = useStore()
const { t } = useI18n()

const availableLists = ref<Audience[]>([])
const cancelEstimateAudienceRequest = ref<any>(null)
const pendingEstimations = ref(0)
const currentSegmentsPage = ref(1)
const globalCost = ref<number | null>(null)
const helpUrl =
  'https://heypongohelp.zendesk.com/hc/fr/articles/22949316359569-Comment-assigner-une-liste-%C3%A0-vos-clients'
const isLoadingEstimation = defineModel<boolean>('isLoadingCount', { default: false })
const lastSegmentsPage = ref<number | null>(null)

const stores = computed(() =>
  getStoresByPermission('campaign.create').map((store) => ({
    value: store.id,
    label: store.name,
    smsName: store.smsName
  }))
)

const storeIds = computed(() => {
  return selectedAudience.value.stores?.length > 0
    ? selectedAudience.value.stores?.map((store) => store.value).join(',')
    : ''
})

const hasProcessingSegments = computed(() => {
  return availableLists.value
    .filter((list) => (selectedAudience.value.lists as number[]).includes(list.id))
    .some((list) => list.is_processing === true)
})

const selectedItemsLabel = computed(() => {
  return selectedAudience.value.stores?.length
    ? t('global.multiselect.stores.storesSelected', {
        count: selectedAudience.value.stores?.length
      })
    : t('pages.campaign.campaignTargetBlock.select.store.mandatory')
})
const selectedStoreIds = computed(() => (selectedAudience.value.stores ?? []).map((store) => store.value))

const includedAudiences = computed(() =>
  resolveIncludedAudiences(availableLists.value, selectedAudience.value.lists as number[])
)
const scopedStoreIds = computed(() => resolveAudienceScopedStoreIds(includedAudiences.value))
const availableStores = computed(() => filterStoresByScope(stores.value, scopedStoreIds.value, (store) => store.value))

const updateStoreIds = (ids: (string | number)[]) => {
  selectedAudience.value.stores = stores.value.filter((store) => ids.includes(store.value))
}

const removeStoreId = (deleteStoreId: number) => {
  const newIds = selectedStoreIds.value.filter((id) => id !== deleteStoreId)
  updateStoreIds(newIds)
}

const estimateAudience = async () => {
  pendingEstimations.value += 1
  isLoadingEstimation.value = true

  try {
    if (cancelEstimateAudienceRequest.value !== null) {
      cancelEstimateAudienceRequest.value.cancel()
    }
    const source = axios.CancelToken.source()
    cancelEstimateAudienceRequest.value = source

    const params: Record<string, unknown> = {
      convert: false,
      campaignType: props.type,
      excludedSegmentIds: selectedAudience.value.excludedLists.join(','),
      segmentIds: selectedAudience.value.lists.join(','),
      storeIds: storeIds.value
    }

    if (props.type !== 'email') {
      params.withPrice = 1
    }

    const response = await axios.get('campaigns/estimated-audience', {
      params,
      cancelToken: source.token
    })

    const { data } = response.data

    count.value = Number(data.optinTargets)
    globalCost.value = data.price / 100
  } catch (error: any) {
    if (axios.isCancel && axios.isCancel(error)) return
  } finally {
    pendingEstimations.value -= 1

    if (pendingEstimations.value <= 0) {
      pendingEstimations.value = 0
      isLoadingEstimation.value = false
    }
  }
}

const fetchSegments = async (searchText?: string) => {
  const params = {
    page: currentSegmentsPage.value,
    pageSize: 1000,
    search: searchText
  }

  try {
    const { data } = await axios.get(`/merchants/${user.value.merchant.id}/segments`, {
      params
    })
    lastSegmentsPage.value = data.meta.last_page
    currentSegmentsPage.value = data.meta.current_page

    const formattedSegments = data.data?.map((s: Audience) => {
      return {
        id: s.id,
        name: s.name,
        type: s.type,
        frequency_type: s.frequency_type,
        is_processing: s.is_processing,
        store_ids: s.store_ids
      }
    })

    if (data.meta.current_page < 2) {
      availableLists.value = [...formattedSegments]
    } else {
      availableLists.value = [...availableLists.value, ...formattedSegments]
    }
  } catch {
    console.error('Error fetching segments')
  }
}

const handleLoadAudiences = (text?: string) => {
  currentSegmentsPage.value++
  if (currentSegmentsPage.value > (lastSegmentsPage.value || 0)) return

  fetchSegments(text)
}
const searchSegmentByName = (text?: string) => {
  currentSegmentsPage.value = 1

  fetchSegments(text)
}

watch(
  () => [selectedAudience.value.stores, selectedAudience.value.lists, selectedAudience.value.excludedLists],
  () => {
    estimateAudience()
  },
  { deep: true, immediate: true }
)

watch(availableLists, () => {
  selectedAudience.value.availableLists = availableLists.value
})

useAudienceStoreScope({
  scopedStoreIds,
  getSelectedIds: () => selectedStoreIds.value,
  setSelectedIds: updateStoreIds,
  getRestoreIds: () => stores.value.map((store) => store.value)
})

onMounted(async () => {
  if (!selectedAudience.value.stores?.length) {
    selectedAudience.value.stores = stores.value
  }

  await fetchSegments()
})
</script>

<template>
  <div class="flex flex-col gap-6">
    <section>
      <div class="rounded-md border bg-white">
        <div class="w-full border-b p-6 text-base font-semibold sm:text-lg">
          {{ t('pages.campaign.campaignTargetBlock.categories.segments') }}
        </div>
        <div class="flex flex-col gap-6 p-6">
          <fieldset class="flex flex-col gap-2">
            <label class="text-sm text-foreground" for="audience">
              {{ t('pages.campaign.campaignTargetBlock.segments.sendTo') }}
            </label>
            <MultiSelectAudiences
              v-model="selectedAudience.lists"
              :audiences="availableLists"
              show-search-bar
              :default-text="t(`pages.campaign.campaignTargetBlock.segments.allCustomers.${type}`)"
              @load-more="handleLoadAudiences($event)"
              @search="searchSegmentByName($event)"
            />
            <a class="text-xs text-foreground-secondary" :href="helpUrl" target="_blank">
              {{ t('pages.campaign.campaignTargetBlock.segments.moreAbout') }}
              <span class="text-primary underline">
                {{ t('pages.campaign.campaignTargetBlock.audience', 2) }}
              </span>
            </a>

            <BaseTip
              v-if="hasProcessingSegments"
              class="mt-2"
              :text="t('pages.campaign.campaignTargetBlock.segments.processingSegments')"
              severity="info"
            />
          </fieldset>

          <fieldset class="flex flex-col gap-2">
            <label class="text-sm text-foreground">
              {{ t('pages.campaign.campaignTargetBlock.select.store.label', { count: availableStores.length }) }}
            </label>
            <BaseMultiSelect
              :model-value="selectedStoreIds"
              :options="availableStores"
              filter
              display="chip"
              :max-selected-labels="4"
              :selected-items-label="selectedItemsLabel"
              :filter-placeholder="t('global.multiselect.stores.searchPlaceholder')"
              :placeholder="t('global.multiselect.stores.placeholder')"
              @update:model-value="updateStoreIds"
            >
              <template #chip="{ value, option }">
                <BaseChip :text="option.label" removable @remove="removeStoreId(value as number)" />
              </template>
            </BaseMultiSelect>
          </fieldset>

          <fieldset class="flex flex-col gap-2">
            <label class="text-sm text-foreground" for="audience">
              {{ t('pages.campaign.campaignTargetBlock.segments.noSendTo') }}
            </label>
            <MultiSelectAudiences
              v-model="selectedAudience.excludedLists"
              :audiences="availableLists"
              show-search-bar
              :default-text="t('pages.campaign.campaignTargetBlock.segments.excludeSegments')"
              @load-more="handleLoadAudiences($event)"
              @search="searchSegmentByName($event)"
            />
          </fieldset>
        </div>
      </div>
    </section>
  </div>
</template>
